// src/components/dashboard components/forms/ResumeForm.jsx

import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Trash2, RefreshCw, Search } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; 
const ResumeForm = () => {
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [error, setError] = useState("");

  // =============================
  // Fetch resumes (Admin)
  // =============================
  const fetchResumes = async () => {
    try {
      setLoading(true);
      setError("");
      const { data } = await axios.get(`${API_BASE_URL}/career/admin/resumes`);
      setResumes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching career resumes:", err);
      setError("Failed to load resumes. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResumes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =============================
  // Delete resume
  // =============================
  const handleDelete = async (id) => {
    if (!id) return;
    if (!window.confirm("Delete this resume permanently?")) return;

    try {
      setDeletingId(id);
      await axios.delete(`${API_BASE_URL}/career/admin/resume/${id}`);
      setResumes((prev) => prev.filter((item) => item.resume_id !== id));
    } catch (err) {
      console.error("Error deleting resume:", err);
      alert("Failed to delete resume. Check console for details.");
    } finally {
      setDeletingId(null);
    }
  };

  // =============================
  // Helpers
  // =============================
  const parseResumeData = (resume) => {
    const raw = resume?.resume_data;
    if (!raw) return null;

    try {
      if (typeof raw === "string") {
        return JSON.parse(raw);
      }
      return raw; // already an object from MySQL JSON
    } catch (e) {
      console.warn("Invalid resume_data JSON for resume_id:", resume.resume_id);
      return null;
    }
  };

  const filteredResumes = useMemo(() => {
    return resumes.filter((item) => {
      const term = search.trim().toLowerCase();

      const matchesSearch =
        !term ||
        item.name?.toLowerCase().includes(term) ||
        item.email?.toLowerCase().includes(term) ||
        item.phone?.toLowerCase().includes(term) ||
        item.role?.toLowerCase().includes(term);

      const matchesRole =
        roleFilter === "All" ||
        (item.role && item.role.toLowerCase() === roleFilter.toLowerCase());

      return matchesSearch && matchesRole;
    });
  }, [resumes, search, roleFilter]);

  // =============================
  // UI
  // =============================
  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 mt-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">
            Career Applications
          </h2>
          <p className="text-sm text-gray-500">
            View, filter, and manage resumes submitted from the careers page.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, phone, role"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none min-w-[220px]"
            />
          </div>

          {/* Role Filter */}
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="All">All Roles</option>
            <option value="Project_Manager">Project Manager</option>
            <option value="Site_Engineer">Site Engineer</option>
            <option value="Architect">Architect</option>
            <option value="Civil_Engineer">Civil Engineer</option>
            <option value="Quantity_Surveyor">Quantity Surveyor</option>
            <option value="Business_Development">Business Development</option>
            <option value="Other">Other</option>
          </select>

          {/* Refresh */}
          <button
            onClick={fetchResumes}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
          >
            <RefreshCw
              className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
            />
            <span>{loading ? "Refreshing..." : "Refresh"}</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Email</th>
              <th className="px-4 py-2 text-left">Phone</th>
              <th className="px-4 py-2 text-left">Role</th>
              <th className="px-4 py-2 text-left">Experience</th>
              <th className="px-4 py-2 text-left">Resume</th>
              <th className="px-4 py-2 text-left">Submitted On</th>
              <th className="px-4 py-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredResumes.length === 0 && !loading && (
              <tr>
                <td
                  colSpan="8"
                  className="px-4 py-6 text-center text-gray-500"
                >
                  No resumes found.
                </td>
              </tr>
            )}

            {filteredResumes.map((resume) => {
              const resumeData = parseResumeData(resume);
              const fileUrl =
                resumeData?.file_url ||
                resumeData?.file_path ||
                resumeData?.path ||
                null;

              return (
                <tr
                  key={resume.resume_id}
                  className="border-t border-gray-200 hover:bg-gray-50"
                >
                  <td className="px-4 py-2">{resume.name || "-"}</td>
                  <td className="px-4 py-2">
                    {resume.email ? (
                      <a
                        href={`mailto:${resume.email}`}
                        className="text-blue-600 hover:underline"
                      >
                        {resume.email}
                      </a>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="px-4 py-2">{resume.phone || "-"}</td>
                  <td className="px-4 py-2">
                    {resume.role?.replace(/_/g, " ") || "-"}
                  </td>
                  <td className="px-4 py-2">
                    {resume.experience || "Not specified"}
                  </td>
                  <td className="px-4 py-2">
                    {fileUrl ? (
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        View / Download
                      </a>
                    ) : (
                      <span className="text-gray-400">No file</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {resume.created_at
                      ? new Date(resume.created_at).toLocaleString()
                      : "-"}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <button
                      onClick={() => handleDelete(resume.resume_id)}
                      disabled={deletingId === resume.resume_id}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}

            {loading && (
              <tr>
                <td
                  colSpan="8"
                  className="px-4 py-4 text-center text-gray-500"
                >
                  Loading resumes...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ResumeForm;
